import {
  DatePickerInput,
  Modal,
  RadioGroup,
  Box,
  Grid
} from '@sequencehq/core-components'
import { Footer } from '../common/Footer'
import { add, format, isBefore, sub } from '@sequencehq/utils/dates'
import { GreyGrey80, Primary13SemiBold } from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useEndOfCurrentBillingPeriod } from '../../../../../../utils/useEndOfNextBillingPeriod'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import {
  CANCEL_MODAL_MIN_HEIGHT_PX,
  DATE_FORMAT
} from '../../scheduleShortcuts.constants'
import { CancelSelection } from '../../scheduleShortcuts.types'
import { useScheduleDates } from '../../../../../../utils/useScheduleDates'

const useCancelScreen = (props: {
  initialSelection: CancelSelection
  onContinue: (selection: CancelSelection) => void
}) => {
  const billingScheduleId = useBillingScheduleId()
  const endOfCurrentBillingPeriodQuery = useEndOfCurrentBillingPeriod({
    billingScheduleId
  })
  const scheduleDates = useScheduleDates({
    billingScheduleId
  })

  const form = useForm<CancelSelection>({
    value: props.initialSelection,
    fieldConfiguration: [
      {
        property: 'cancelDateType',
        validation: [required]
      },
      {
        property: 'customDate',
        disabled: ctx => ctx.formData.cancelDateType !== 'customDate',
        validation: [required]
      }
    ]
  })

  const onSubmit = () => {
    props.onContinue(form.queries.formData)
  }

  const isFutureStartingSchedule =
    scheduleDates.startDate && isBefore(new Date(), scheduleDates.startDate)

  /**
   * The minimum end date can only ever be the next day after the start date, as our
   * minimum viable phase duration is 2 days (the start date, plus the day after).
   */
  const minimumCustomDate = add(
    (isFutureStartingSchedule
      ? scheduleDates.startDate
      : scheduleDates.currentPhaseStartDate) ?? new Date(),
    { days: 1 }
  )

  /**
   * The last selectable end date is the day before the schedule's current end
   * date — ending it on the date it already ends on leaves no phase to truncate.
   */
  const maximumCustomDate =
    scheduleDates.endDate && sub(scheduleDates.endDate, { days: 1 })

  return {
    isLoading: endOfCurrentBillingPeriodQuery.isLoading,
    endOfBillingPeriod: endOfCurrentBillingPeriodQuery.endOfBillingPeriod,
    scheduleAlreadyEndsInCurrentPeriod:
      endOfCurrentBillingPeriodQuery.scheduleAlreadyEndsInCurrentPeriod,
    minimumCustomDate,
    maximumCustomDate,
    formFields: form.fields,
    isValid: form.queries.isValid,
    onSubmit
  }
}

export const CancelScreen = (props: {
  initialSelection: CancelSelection
  onBack: () => void
  onContinue: (selection: CancelSelection) => void
}) => {
  const {
    isLoading,
    endOfBillingPeriod,
    scheduleAlreadyEndsInCurrentPeriod,
    formFields,
    isValid,
    onSubmit,
    minimumCustomDate,
    maximumCustomDate
  } = useCancelScreen({
    initialSelection: props.initialSelection,
    onContinue: props.onContinue
  })

  const isEndOfBillingPeriodDisabled = !endOfBillingPeriod || isLoading
  const endOfBillingPeriodDisabledReason = scheduleAlreadyEndsInCurrentPeriod
    ? 'This schedule already ends at the end of the current billing period'
    : 'Could not calculate next billing date'

  return (
    <>
      <Modal.Content>
        <Grid
          gridTemplateRows="auto auto auto"
          gap="12px"
          minHeight={`${CANCEL_MODAL_MIN_HEIGHT_PX}px`}
        >
          <Box {...Primary13SemiBold} color={GreyGrey80}>
            When do you want to this schedule to end?
          </Box>
          <RadioGroup
            name="cancelDateType"
            onChange={newValue =>
              formFields.cancelDateType.onChange(
                newValue as CancelSelection['cancelDateType']
              )
            }
            value={formFields.cancelDateType.value}
            options={[
              {
                label: 'Today',
                description: 'End schedule today.',
                labelAnnotation:
                  formFields.cancelDateType.value === 'immediate'
                    ? format(new Date(), DATE_FORMAT)
                    : '',
                value: 'immediate'
              },
              {
                label: 'End of current billing period',
                description:
                  'End schedule at the end of the current billing period.',
                labelAnnotation:
                  formFields.cancelDateType.value === 'endOfBillingPeriod' &&
                  endOfBillingPeriod
                    ? format(endOfBillingPeriod, DATE_FORMAT)
                    : '',
                value: 'endOfBillingPeriod',
                disabled: isEndOfBillingPeriodDisabled,
                disabledReason: isEndOfBillingPeriodDisabled
                  ? endOfBillingPeriodDisabledReason
                  : undefined
              },
              {
                label: 'Custom date',
                description: 'End schedule on a custom date.',
                value: 'customDate',
                additionalComponent:
                  formFields.cancelDateType.value === 'customDate' ? (
                    <Box paddingTop="12px">
                      <DatePickerInput
                        minDate={minimumCustomDate}
                        maxDate={maximumCustomDate}
                        width="180px"
                        value={formFields.customDate.value}
                        onChange={newDate =>
                          formFields.customDate.onChange(newDate)
                        }
                      />
                    </Box>
                  ) : null
              }
            ]}
          />
        </Grid>
      </Modal.Content>
      <Footer onContinue={onSubmit} onBack={props.onBack} disabled={!isValid} />
    </>
  )
}
